import React, { Fragment, useMemo, useState } from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { Box, Typography } from '@mui/material'
import { Avatar, AvatarSize, Chip, DataGrid, Icon, PopUp, theme } from '@ketch-com/deck'
import { EmptyValueRenderer } from 'components/EmptyValueRenderer'
import { PermitResultStatus } from 'pages/insights/auditLog/components/PermitResultStatus'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import {
  GetConsentStatusResponseBodyPurposesItemDTO,
  PermitTransactionV2OrchestrationsDTO,
} from '@ketch-com/figurehead'
import { CanonicalPurpose, CanonicalPurposeName } from 'utils/constants/canonicalPurposes'
import { NoDataEmptyState } from 'pages/insights/auditLog/components/NoDataEmptyState'

type ListItemConnectionsProps = {
  purpose: GetConsentStatusResponseBodyPurposesItemDTO
}

export const ListItemConnections: React.FC<ListItemConnectionsProps> = ({ purpose }) => {
  const { consentTransactionData } = useAuditLogContext()
  const [actionsModalOpen, setActionsModalOpen] = useState(false)
  const [actionsModalSystemName, setActionsModalSystemName] = useState('')
  const [modalActions, setModalActions] = useState<string[]>([])

  const columns: GridColDef<PermitTransactionV2OrchestrationsDTO>[] = useMemo(() => {
    return [
      {
        align: 'left',
        field: 'system',
        width: 285,
        headerAlign: 'left',
        headerName: 'System',
        renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
        sortable: false,
        renderCell: ({ row: connection }: GridRenderCellParams<PermitTransactionV2OrchestrationsDTO>) => {
          const hasMultipleIdentifiers = connection.identities?.length && connection.identities?.length > 1
          return (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              py={1}
              id={connection.connectionID}
              sx={
                hasMultipleIdentifiers
                  ? {
                      p: '0px 0px 6px 12px !important',
                      width: '100%',
                      ml: '6px',
                    }
                  : undefined
              }
            >
              <Avatar
                src={connection.logoURL}
                size={AvatarSize.medium}
                variant="circular"
                borderColor={theme.palette.Common.Divider}
                sx={{ p: 0.75 }}
              />
              <Box display="flex" flexDirection="column">
                <Typography variant="label">{connection.dataSystemName}</Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Icon name="OConnectionTrue" width={20} height={20} iconColor={theme.palette.Success.PrimaryDark} />
                  <Typography variant="smallLabel" color={theme.palette.Success.PrimaryDark}>
                    {connection.connectionName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )
        },
      },
      {
        align: 'left',
        field: 'identifier',
        width: 240,
        headerAlign: 'left',
        headerName: 'Identifier',
        renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
        sortable: false,
        renderCell: ({ row: connection }: GridRenderCellParams<PermitTransactionV2OrchestrationsDTO>) => {
          const hasMultipleIdentifiers = connection.identities?.length && connection.identities?.length > 1
          return connection.identities?.length ? (
            <Box display="flex" flexDirection="column" gap={0.65} my={0.25} width="100%">
              {connection.identities?.map((identifier, index) => {
                return (
                  <Fragment key={`${connection.connectionCode}-${identifier.space}`}>
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      justifyContent="center"
                      flexDirection="column"
                      width="100%"
                      sx={
                        hasMultipleIdentifiers
                          ? {
                              borderTop: index !== 0 ? `1.5px solid ${theme.palette.Common.Divider}` : 'none',
                              p: '0px 0px 0px 12px',
                              paddingTop: index !== 0 ? 1.25 : 0,
                            }
                          : undefined
                      }
                    >
                      <Typography variant="body">{identifier.name}</Typography>
                      <Typography variant="smallBody" color={theme.palette.Text.Secondary}>
                        {identifier.space}
                      </Typography>
                    </Box>
                  </Fragment>
                )
              })}
            </Box>
          ) : (
            <EmptyValueRenderer />
          )
        },
      },
      {
        align: 'left',
        field: 'identifierValue',
        width: 340,
        headerAlign: 'left',
        headerName: 'Identifier Value',
        renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
        sortable: false,
        renderCell: ({ row: connection }: GridRenderCellParams<PermitTransactionV2OrchestrationsDTO>) => {
          const hasMultipleIdentifiers = connection.identities?.length && connection.identities?.length > 1
          return connection.identities?.length ? (
            <Box display="flex" flexDirection="column" gap={0.65} my={0.25} width="100%">
              {connection.identities?.map((identifier, index) => {
                return (
                  <Fragment key={`${connection.connectionCode}-${identifier.value}`}>
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      justifyContent="center"
                      flexDirection="column"
                      width="100%"
                      sx={
                        hasMultipleIdentifiers
                          ? {
                              borderTop: index !== 0 ? `1.5px solid ${theme.palette.Common.Divider}` : 'none',
                              p: '0px 0px 8px 12px',
                              paddingTop: index !== 0 ? 2.5 : 0,
                              paddingBottom: index !== 0 ? 1 : 0,
                              minHeight: '37px',
                              height: index !== 0 ? '48.21px' : 'undefined',
                            }
                          : undefined
                      }
                    >
                      <Typography variant="body">{identifier.value}</Typography>
                    </Box>
                  </Fragment>
                )
              })}
            </Box>
          ) : (
            <EmptyValueRenderer />
          )
        },
      },
      {
        align: 'left',
        field: 'action',
        width: 118,
        headerAlign: 'left',
        headerName: 'Actions',
        renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
        sortable: false,
        renderCell: ({ row: connection }: GridRenderCellParams<PermitTransactionV2OrchestrationsDTO>) => {
          const hasMultipleIdentifiers = connection.identities?.length && connection.identities?.length > 1

          const handleActionsClick = () => {
            setModalActions(connection.purposes || [])
            setActionsModalSystemName(connection.dataSystemName || 'System')
            setActionsModalOpen(true)
          }

          return (
            <Box
              sx={
                hasMultipleIdentifiers
                  ? {
                      p: '6px 0px 14.5px 18px !important',
                      borderBottom: `1.5px solid ${theme.palette.Common.Divider}`,
                      width: '100%',
                    }
                  : undefined
              }
            >
              {connection.purposes?.length ? (
                <Chip
                  size="small"
                  label={connection.purposes?.length}
                  onClick={handleActionsClick}
                  onDelete={handleActionsClick}
                  clickable
                  deleteIcon={<Icon name="OArrowCRight" />}
                />
              ) : (
                <EmptyValueRenderer />
              )}
            </Box>
          )
        },
      },
      {
        align: 'left',
        field: 'result',
        width: 166,
        headerAlign: 'left',
        headerName: 'Result',
        renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
        sortable: false,
        renderCell: ({ row: connection }: GridRenderCellParams<PermitTransactionV2OrchestrationsDTO>) => {
          const hasMultipleIdentifiers = connection.identities?.length && connection.identities?.length > 1
          return (
            <Box
              sx={
                hasMultipleIdentifiers
                  ? {
                      p: '6px 0px 14.5px 18px !important',
                      borderBottom: `1.5px solid ${theme.palette.Common.Divider}`,
                      width: '100%',
                    }
                  : undefined
              }
            >
              <PermitResultStatus result={connection.result} />
            </Box>
          )
        },
      },
    ]
  }, [setActionsModalOpen, setActionsModalSystemName, setModalActions])

  // Get connections for this purpose
  const purposeConnections = useMemo(() => {
    const transaction = consentTransactionData.find(transaction => transaction.transactionID === purpose.transactionID)

    if (!transaction) {
      return []
    }

    const connections: PermitTransactionV2OrchestrationsDTO[] = []
    purpose?.canonicalPurposes?.forEach(canonicalPurpose => {
      const connectionCodes = transaction.purposes?.[canonicalPurpose]
      connectionCodes?.forEach(connectionCode => {
        const connection = transaction.orchestrations?.[connectionCode]
        if (connection) {
          connections.push(connection)
        }
      })
    })

    // Sort connection by systemName, or systemCode if systemName is not available
    return connections.sort((a, b) => {
      if (a.dataSystemName && b.dataSystemName) {
        return a.dataSystemName.localeCompare(b.dataSystemName)
      }
      return a.connectionCode?.localeCompare(b.connectionCode || '') || 0
    })
  }, [purpose, consentTransactionData])

  const showPagination = purposeConnections.length > 5

  if (!purposeConnections.length) {
    return (
      <NoDataEmptyState
        title="No System Data"
        subtitle="We couldn't find any system orchestration data for this purpose. This may be due to consent having just been set, or it was set before orchestration data was tracked."
      />
    )
  }

  return (
    <>
      <DataGrid
        autosizeOnMount
        autosizeOptions={{
          expand: true,
          includeHeaders: true,
          includeOutliers: false,
        }}
        getRowHeight={() => 'auto'}
        getRowId={(row: PermitTransactionV2OrchestrationsDTO) => row.connectionCode || ''}
        columns={columns}
        rows={purposeConnections}
        checkboxSelection={false}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnPinning
        disableColumnReorder
        disableColumnResize
        disableChildrenSorting={false}
        disableRowHoverStates
        paginationMode="client"
        rowCount={purposeConnections.length}
        pagination={showPagination}
        hideFooter={!showPagination}
        pageSizeOptions={[5, 10, 20]}
        disableBorder
        noRowsOverlayProps={{ buttonTitle: '' }}
        getCellClassName={params => {
          let extraClassname = ''
          if (params.row.identities?.length > 1) {
            extraClassname = 'system-connection-cell-multi-actions'
          }
          return extraClassname
        }}
        sx={{
          '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
            backgroundImage: 'unset',
            cursor: 'auto',
          },
          // Remove 5th column that MUI is adding
          '& .MuiDataGrid-row > :nth-child(6)': {
            display: 'none',
          },
          '.system-connection-cell-multi-actions': {
            py: 1,
            px: 0,
            alignItems: 'flex-start',
            width: '100%',
          },
        }}
      />
      {actionsModalOpen && (
        <PopUp
          title={actionsModalSystemName + ' Actions'}
          variant="dialog"
          onClose={() => setActionsModalOpen(false)}
          popUpWidth={'500px'}
        >
          <Box display="flex" flexDirection="column" gap={2} pb={3} width="100%">
            {modalActions.map(canonicalPurpose => {
              return (
                <Box
                  display="flex"
                  alignItems="center"
                  width="100%"
                  gap={1}
                  key={`${purpose.code}-${canonicalPurpose}`}
                >
                  <Chip size="medium" label={CanonicalPurposeName[canonicalPurpose as CanonicalPurpose]} />
                  <Icon name="OArrowRight" iconColor={theme.palette.Black.o48} />
                  <Typography variant="body" fontWeight={700}>
                    {purpose.allowed ? 'Opt In' : 'Opt Out'}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </PopUp>
      )}
    </>
  )
}
